import { useState, useEffect, type RefObject } from 'react'

interface UseBackgroundContrastOptions {
  containerRef?: RefObject<HTMLElement | null>
}

/**
 * Detects whether the background behind a given element is dark or light.
 * Samples the pixel color from a canvas snapshot of the area behind the element.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
  options: UseBackgroundContrastOptions = {}
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const check = () => {
      const rect = element.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2

      // Hide the entire container (not just the button) so elementFromPoint
      // can reach the actual page content behind the fixed overlay
      const container = options.containerRef?.current
      const hiddenEl = element.style.visibility
      const hiddenContainer = container ? container.style.visibility : ''
      element.style.visibility = 'hidden'
      if (container) container.style.visibility = 'hidden'
      const behind = document.elementFromPoint(x, y)
      element.style.visibility = hiddenEl
      if (container) container.style.visibility = hiddenContainer

      if (!behind) return

      // Returns perceptual lightness (0–1) and alpha, or null if unparseable
      const parseLightness = (color: string): { lightness: number; alpha: number } | null => {
        // oklab(L a b / alpha) — L is directly perceptual lightness 0–1
        const oklab = color.match(/oklab\(\s*([\d.]+)\s+[-\d.]+\s+[-\d.]+(?:\s*\/\s*([\d.]+))?\s*\)/)
        if (oklab) {
          return { lightness: Number(oklab[1]), alpha: oklab[2] !== undefined ? Number(oklab[2]) : 1 }
        }
        // rgb(r g b / a) or rgba(r, g, b, a)
        const rgb = color.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[/\s,]+([\d.]+))?\s*\)/)
        if (rgb) {
          const [r, g, b] = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]
          return { lightness: (0.299 * r + 0.587 * g + 0.114 * b) / 255, alpha: rgb[4] !== undefined ? Number(rgb[4]) : 1 }
        }
        return null
      }

      // Walk up the DOM tree to find the first ancestor with a visible background
      let node: Element | null = behind
      while (node) {
        const style = getComputedStyle(node)

        // Check CSS gradients in backgroundImage first
        const bgImage = style.backgroundImage
        if (bgImage && bgImage !== 'none') {
          // Match both oklab() and rgb()/rgba() color functions in gradients
          const colorMatches = [...bgImage.matchAll(/(?:oklab|rgba?)\([^)]+\)/g)]
          const parsed = colorMatches.map(c => parseLightness(c[0])).filter((c): c is { lightness: number; alpha: number } => c !== null && c.alpha > 0.1)
          if (parsed.length > 0) {
            const avgLightness = parsed.reduce((sum, c) => sum + c.lightness, 0) / parsed.length
            setBackground(avgLightness < 0.5 ? 'dark' : 'light')
            return
          }
        }

        // Check solid backgroundColor
        const bgColor = parseLightness(style.backgroundColor)
        if (bgColor && bgColor.alpha > 0.1) {
          setBackground(bgColor.lightness < 0.5 ? 'dark' : 'light')
          return
        }

        node = node.parentElement
      }
    }

    check()

    const observer = new IntersectionObserver(check, {
      root: options.containerRef?.current,
      threshold: [0, 0.5, 1],
    })
    observer.observe(element)

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [elementRef, options.containerRef])

  return background
}
